import Grid from "@mui/material/Grid";
import NTMXGrid from "../components/NTMXGrid";
import React, { useContext, useMemo, useState } from "react";
import { Divider } from "@mui/material";
import StartIconButton from "../components/buttons/StartIconButton";
import CreateIcon from '@mui/icons-material/Create';
import CreateRankModal from "../components/modals/CreateRankModal";
import { deleteElem, getErrorMessage, ORGANIZATIONS, post, put, RANKINGS, RANKS } from "../services/Client";
import { useSnackbar } from "notistack";
import { useQueryClient } from "react-query";
import useGetSessionType from "../constants/sessionType";
import RenderCell from "../components/cellRender/RenderCell";
import dayjs from "dayjs";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RankAward from "./Rankings/RankAward";
import InfoRepetitionRankings from "../components/cellRender/InfoRepetitionRankings";
import RankInfo from "./Rankings/RankInfo";
import IconButton from "@mui/material/IconButton";
import useGetUsefulValues from "../constants/rankingUsefulValues";
import useGetRankingFilter, { RankingFilter } from "../constants/rankingFilter";
import { useTranslation } from "react-i18next";
import RenderCellAgeInterval from "../components/cellRender/RenderCellAgeInterval";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmIconButton from "../components/buttons/ConfirmIconButton";
import { genderCode } from "../constants/genderCode";
import RenderCellRankFilter from "../components/cellRender/RenderCellRankFilter";
import { KeyboardTab } from "@mui/icons-material";
import RenderCellSessionTypes from "../components/cellRender/RenderCellSessionTypes";
import RenderSessionTypeAndClusterInterval from "../components/cellRender/RenderSessionTypeAndClusterInterval";
import { defineEditPermissions, EditPermissionType, isAdmin as getIsAdmin } from "services/PermissionManager";
import { UserContext } from "./App";
import { useRankingFilterValueMapper } from "services/RankingFilterHelper";
import RenderCellAwardsLimitForGroup from "components/cellRender/RenderCellAwardsLimitForGroup";

export default function RanksManager({ ranks, loading, national = false, organizationId, customField = [] }) {

    const isAdmin = getIsAdmin(useContext(UserContext));
    let [isCreating, setIsCreating] = useState(false);
    let [showInfo, setShowInfo] = useState(null);
    let [showAwards, setShowAwards] = useState(null);
    const { t } = useTranslation();
    const usefulValues = useGetUsefulValues(national);
    const rankingFilter = useGetRankingFilter();
    const { enqueueSnackbar } = useSnackbar();
    let queryClient = useQueryClient();
    const today = React.useMemo(() => dayjs(new Date()).format("YYYY-MM-DD"), []);
    const sessionType = useGetSessionType();
    const warningRankAlreadyStarted = React.useMemo(() => t('warningRankAlreadyStarted'),[t])
    const filterValueMapperFn = useRankingFilterValueMapper({ customField });
    const sortedRanks = React.useMemo(() => ranks.slice().sort((a, b) => a.startDate - b.startDate), [ranks]);
    const unexpiredRanks = React.useMemo(() => sortedRanks?.length > 0 && sortedRanks.filter((e) => dayjs(e.endDate).endOf('day') >= Date.now()), [sortedRanks]);
    const expiredRanks = React.useMemo(() => sortedRanks?.length > 0 && sortedRanks.filter((e) => dayjs(e.endDate).endOf('day') < Date.now()), [sortedRanks]);
    const key = React.useMemo(() => national ? ["National", RANKS] : [ORGANIZATIONS, { id: organizationId }, RANKINGS], [national, organizationId]);
    const editPermissions = useMemo(
        () => defineEditPermissions(isAdmin, EditPermissionType.RANKING, ranks),
        [isAdmin, ranks]
    );

    const deleteRank = React.useCallback((id) => {
        enqueueSnackbar(t('saving...'), { variant: "info" });
        deleteElem(RANKINGS, { elem: id, params: { force: false } })
            .then(() => enqueueSnackbar(t('saved'), { variant: "success" }))
            .catch(e => enqueueSnackbar(getErrorMessage(e), { variant: "error" }))
            .finally(() => queryClient.invalidateQueries(key));
    }, [enqueueSnackbar, key, queryClient, t]);

    const closeRank = React.useCallback((id) => {
        enqueueSnackbar(t('saving...'), { variant: "info" });
        post(`${RANKINGS}/${id}/generate-next-repetition`)
            .then(() => enqueueSnackbar(t('closedRank'), { variant: "success" }))
            .catch(e => enqueueSnackbar(getErrorMessage(e), { variant: "error" }))
            .finally(() => queryClient.invalidateQueries(key));
    },[enqueueSnackbar, key, queryClient, t]);

    const createRank = React.useCallback((values) => {
        if (!national) {
            values.organization = Number.parseInt(organizationId);
        }
        enqueueSnackbar(t('saving...'), { variant: "info" });
        post(RANKINGS, { body: values })
            .then(() => enqueueSnackbar(t('saved'), { variant: "success" }))
            .catch(e => enqueueSnackbar(getErrorMessage(e), { variant: "error" }))
            .finally(() => queryClient.invalidateQueries(key));
    },[enqueueSnackbar, key, national, organizationId, queryClient, t]);

    const saveEditRank = React.useCallback((id, body) => {
        enqueueSnackbar(t('saving...'), { variant: "info" });
        put(RANKINGS, { body, elem: id })
            .then(() => enqueueSnackbar(t('saved'), { variant: "success" }))
            .catch(e => enqueueSnackbar(getErrorMessage(e), { variant: "error" }))
            .finally(() => queryClient.invalidateQueries(key));
    }, [enqueueSnackbar, key, queryClient, t]);

    const saveEdit = React.useCallback((id, field, newValue) => {
        if (field === "startDate" || field === "endDate") newValue = new Date(newValue).getTime()
        saveEditRank(id, { [field]: newValue })
    }, [saveEditRank]);

    const defaultColumns = React.useMemo(() => ([
        {
            headerName: t('title'),
            field: 'title',
            width: 200,
            renderCell: (params) =>
                <RenderCell
                    params={params}
                    saveEdit={editPermissions[params.row.id].canEditTitle && saveEdit}
                    required
                    warningMessage={params.row.startDate < Date.now() && warningRankAlreadyStarted}
                />
        },
        {
            headerName: t('value'),
            field: 'value',
            width: 200,
            valueGetter: (params) => usefulValues.find(elem => elem.id === params.value) ? usefulValues.find(elem => elem.id === params.value)?.name : "",
            renderCell: (params) =>
                <RenderCell
                    params={params}
                    saveEdit={editPermissions[params.row.id].canEditValue && saveEdit}
                    type="select"
                    options={usefulValues}
                    warningMessage={params.row.startDate < Date.now() && warningRankAlreadyStarted}
                    required
                />
        },
        {
            headerName: t('filter'),
            field: 'filter',
            width: 200,
            valueGetter: (params) => (params.value !== null && params.value !== undefined) ? rankingFilter.find(elem => elem.id === params.value)?.name : "",
            renderCell: (params) =>
                <RenderCellRankFilter
                    params={params}
                    saveEdit={editPermissions[params.row.id].canEditFilter && saveEditRank}
                    rankingFilter={rankingFilter}
                    warningMessage={params.row.startDate < Date.now() && warningRankAlreadyStarted}
                />
        },
        {
            headerName: t('valueFilter'),
            field: 'filterValue',
            width: 200,
            valueGetter: (params) => filterValueMapperFn(params.row.filter, params.value),
            renderCell: (params) => {
                const warningMessage = params.row.startDate < Date.now() && warningRankAlreadyStarted;
                const saveEditFilterValue = editPermissions[params.row.id].canEditFilterValue && saveEdit;
                const filterType = params.row.filter;
                const cellToRender = {
                    [RankingFilter.TYPE.id]: () => (
                        <RenderCellSessionTypes
                            params={params}
                            saveEdit={saveEditFilterValue}
                            warningMessage={warningMessage}
                        />
                    ),
                    [RankingFilter.AGE.id]: () => (
                        <RenderCellAgeInterval
                            params={params}
                            saveEdit={saveEditFilterValue}
                            warningMessage={warningMessage}
                        />
                    ),
                    [RankingFilter.GENDER.id]: () => (
                        <RenderCell
                            params={params}
                            saveEdit={saveEditFilterValue}
                            type="select"
                            options={Object.values(genderCode).map(v => ({
                                id: v.id,
                                name: t(v.value)
                            }))}
                            warningMessage={warningMessage}
                            required
                        />
                    ),
                    [RankingFilter.JOLLY.id]: () => (
                        <RenderCell
                            params={params}
                            saveEdit={saveEditFilterValue}
                            type="select"
                            options={Object.values(customField).map(v => ({
                                id: v.id,
                                name: t(v.name)
                            }))}
                            warningMessage={warningMessage}
                            required
                        />
                    ),
                    [RankingFilter.TYPE_JOLLY.id]: () => (
                        <RenderSessionTypeAndClusterInterval
                            params={params}
                            saveEdit={saveEditFilterValue}
                            warningMessage={warningMessage}
                            customField={customField}
                        />
                    )
                }
                return cellToRender[filterType]?.apply() ?? undefined;
            }
        },
        {
            headerName: t('month'),
            field: 'month',
            width: 100,
            valueGetter: ({ row, field }) => {
                if (row.startDate) {
                    const month = new Date(row.startDate).toLocaleString('en-EN', { month: 'long' }).toLowerCase()
                    return t(month);
                }
                else return ""
            }
        },
        {
            headerName: t('year'),
            field: 'year',
            width: 100,
            valueGetter: ({ row, field }) => {
                if (row.startDate) return new Date(row.startDate).getFullYear()
                else return ""
            }
        },
        {
            headerName: t('startDate'),
            field: 'startDate',
            width: 200,
            renderCell: (params) =>
                <RenderCell
                    params={params}
                    saveEdit={editPermissions[params.row.id].canEditStartDate && saveEdit}
                    required type="date"
                />,
        },
        {
            headerName: t('endDate'),
            field: 'endDate',
            width: 200,
            renderCell: (params) =>
                <RenderCell
                    params={params}
                    saveEdit={editPermissions[params.row.id].canEditEndDate && saveEdit}
                    required type="date" min={today}
                    warningMessage={params.row.startDate < Date.now() && warningRankAlreadyStarted}
                />,
        },
        {
            headerName: t('repetitions'),
            field: 'repeatNum',
            width: 200,
            renderCell: (params) => {
                const canCloseRanking = editPermissions[params.row.id].canCloseRanking;
                const canEditRepetitions = editPermissions[params.row.id].canEditRepetitions;
                return <>
                    {
                        params.row.repeatType !== 0 &&
                        <>
                            <RenderCell
                                params={params}
                                required
                                type="number"
                                warningMessage={params.row.startDate < Date.now() && warningRankAlreadyStarted}
                                saveEdit={canEditRepetitions && saveEdit}
                            />
                            <InfoRepetitionRankings params={params} showValue={false} modalTitle={t('ranksPreview')} />
                        </>
                    }
                    {
                        canCloseRanking &&
                        <ConfirmIconButton
                            onConfirm={() => canCloseRanking && closeRank(params.row.id)}
                            title={t('closeRankTitle')}
                            text={t('closeRankText')}
                        >
                            <KeyboardTab />
                        </ConfirmIconButton>
                    }
                </>
            }
        },
        {
            headerName: t('awardLimit'),
            field: 'awardsLimitForGroup',
            width: 160,
            renderCell: (params) => (
                <RenderCellAwardsLimitForGroup
                    params={params}
                    infoText={t('awardsLimitForGroupInfo')}
                />
            )
        },
        {
            headerName: t('awardsValueLimitForGroup'),
            field: 'awardsValueLimitForGroup',
            width: 160,
            renderCell: (params) => (
                <RenderCellAwardsLimitForGroup
                    params={params}
                    infoText={t('awardsValueLimitForGroupInfo')}
                />
            )
        },
        {
            headerName: ' ',
            field: "info",
            width: 160,
            renderCell: (params) => {
                const canDeleteRanking = editPermissions[params.row.id].canDeleteRanking;
                return <>
                    <IconButton onClick={() => setShowInfo(params.row)} size="large"> <VisibilityIcon /> </IconButton>
                    <IconButton onClick={() => setShowAwards(params.row)} size="large"> <EmojiEventsIcon /> </IconButton>
                    {/* params.row.startDate > Date.now() && */
                        canDeleteRanking &&
                        <ConfirmIconButton
                            onConfirm={() => canDeleteRanking && deleteRank(params.row.id)}
                            title={t('confirmDeletion')}
                            text={t('requestDefinitiveEliminationRank')}
                        >
                            <DeleteIcon />
                        </ConfirmIconButton>
                    }
                </>
            }

        }
    ]), [
        customField,
        today,
        usefulValues,
        warningRankAlreadyStarted,
        editPermissions,
        filterValueMapperFn,
        rankingFilter,
        closeRank,
        deleteRank,
        saveEdit,
        saveEditRank,
        t
    ]
    );

    if (showInfo) return <RankInfo goBack={() => setShowInfo(null)} rankId={showInfo.id} national={national}
        customField={customField} queryClientKey={key} />
    if (showAwards) return <RankAward goBack={() => setShowAwards(null)} rank={showAwards} />

    return <Grid>
        <NTMXGrid
            key={"ranks-manager-table-active"}
            columns={defaultColumns}
            rows={unexpiredRanks || []}
            title={t('unexpiredRanks')}
            loading={loading}
            rightButton={
                <Grid container justifyContent={"flex-end"}>
                    <StartIconButton
                        disabled={
                            !editPermissions[EditPermissionType.RANKING.canCreatePermissionKey]
                        }
                        onClick={() => setIsCreating(true)} title={t('createRanks')}
                        startIcon={<CreateIcon />}
                    />
                </Grid>
            }
        />
        <Divider />
        <NTMXGrid
            key={"ranks-manager-table-expired"}
            columns={defaultColumns}
            rows={expiredRanks || []}
            loading={loading}
            title={t('expiredRanks')}
        />
        <CreateRankModal open={!!isCreating} onClose={() => setIsCreating(false)} onSubmit={createRank}
            usefulValues={usefulValues} rankingFilter={rankingFilter} customField={customField} />
    </Grid>

}
